/**
 * アプリから利用者へ「お願い」するものの記録と判断（iOS アプリだけで動く）。
 *
 * - ストアのレビュー依頼：県メダル（銅以上）を初めて取ったあとに、1回だけ
 * - 今日の県のお知らせ：今日の県を2日目に遊び終えたとき、1回だけ「お知らせしますか？」と聞く。
 *   OK なら毎朝8時ごろの通知を端末内に予約する（外へは何も送らない）
 *
 * どちらも Platform 側で iOS 以外は何もしない（Web・モック・Android）。
 */

import { metaStorageKey } from '../game/config/gameConfig';
import type { StorageLike } from '../game/systems/StorageSystem';
import type { LocalNotice, Platform } from '../platform/Platform';
import { MEDAL_RANK, type PrefMedal } from './trophies';
import { todaysDaily, type TodaysDaily } from './dailyStage';
import { JsonStore } from './jsonStore';
import { track } from './telemetry';

export interface AppPromptsData {
  /** レビュー依頼を出したか（1回だけ）。 */
  reviewAsked: boolean;
  /** 「今日の県のお知らせ」を聞いたか（1回だけ）。 */
  noticeAsked: boolean;
  /** お知らせを受け取る設定か。 */
  noticeEnabled: boolean;
}

export function normalizeAppPrompts(raw: unknown): AppPromptsData {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    reviewAsked: r.reviewAsked === true,
    noticeAsked: r.noticeAsked === true,
    noticeEnabled: r.noticeEnabled === true,
  };
}

/** お知らせの時刻（端末の現地時間）。 */
export const NOTICE_HOUR = 8;
/** 何日先まで予約しておくか。起動のたびに予約し直す。 */
export const NOTICE_DAYS = 7;

/**
 * レビュー依頼を出すか。
 * - このプレイで、この県のメダルが上がって銅以上になった（＝初めて取った色）
 * - この県を2回以上遊んでいる（はじめての1回目の直後は避ける）
 * - まだ一度も出していない
 */
export function shouldRequestReview(input: {
  before: PrefMedal;
  after: PrefMedal;
  playCount: number;
  alreadyAsked: boolean;
}): boolean {
  if (input.alreadyAsked) return false;
  if (input.playCount < 2) return false;
  return MEDAL_RANK[input.after] >= MEDAL_RANK.bronze && MEDAL_RANK[input.after] > MEDAL_RANK[input.before];
}

/** 予約の番号。日付から決まる（同じ日の予約は入れ替わる）。例: 2026-10-05 → 20261005 */
export function noticeIdFor(dateKey: string): number {
  return Number(dateKey.replace(/-/g, ''));
}

/**
 * これから先の「今日の県」のお知らせ（今日の8時を過ぎていれば明日から）。
 * 今日の県の決め方は dailyStage（全員同じ）をそのまま使う。
 */
export function buildDailyNotices(
  now: Date = new Date(),
  days: number = NOTICE_DAYS,
  pick: (date: Date) => TodaysDaily | null = todaysDaily,
): LocalNotice[] {
  const list: LocalNotice[] = [];
  for (let i = 0; list.length < days && i <= days; i += 1) {
    const at = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, NOTICE_HOUR, 0, 0, 0);
    if (at.getTime() <= now.getTime()) continue;
    const today = pick(at);
    if (!today) continue;
    list.push({
      id: noticeIdFor(today.dateKey),
      title: '今日の県',
      body: `今日の県は${today.regionName}です。全員同じ順番で料理が落ちてきます。`,
      at,
      href: today.href,
    });
  }
  return list;
}

export class AppPrompts {
  private readonly store: JsonStore<AppPromptsData>;

  constructor(
    private readonly platform: Platform,
    storage?: StorageLike | null,
  ) {
    this.store = new JsonStore(metaStorageKey('app-prompts'), normalizeAppPrompts, storage);
  }

  get data(): Readonly<AppPromptsData> {
    return this.store.data;
  }

  /* ---------------- レビュー依頼 ---------------- */

  /**
   * 条件を満たしていればレビュー依頼を出す。出したら true。
   * 配信モードのあいだは出さない（OS のレビュー画面が配信に映るため。出さなかった回は「まだ」のまま）。
   */
  maybeRequestReview(input: {
    before: PrefMedal;
    after: PrefMedal;
    playCount: number;
    streamerMode?: boolean;
  }): boolean {
    if (this.platform.kind !== 'native') return false;
    if (input.streamerMode) return false;
    if (!shouldRequestReview({ ...input, alreadyAsked: this.store.data.reviewAsked })) return false;
    this.store.update((d) => {
      d.reviewAsked = true;
    });
    track('review_request');
    void this.platform.requestReview().catch(() => undefined);
    return true;
  }

  /* ---------------- 今日の県のお知らせ ---------------- */

  get canNotify(): boolean {
    return this.platform.canNotify;
  }

  get noticeEnabled(): boolean {
    return this.store.data.noticeEnabled;
  }

  /** 今日の県を遊び終えた結果画面で聞くか（2日目以降・まだ聞いていない）。 */
  shouldAskNotice(dailyDaysPlayed: number): boolean {
    return this.canNotify && !this.store.data.noticeAsked && !this.store.data.noticeEnabled && dailyDaysPlayed >= 2;
  }

  /** 聞いたことを記録する（答えがどちらでも、二度は聞かない）。 */
  markNoticeAsked(): void {
    this.store.update((d) => {
      d.noticeAsked = true;
    });
  }

  /**
   * お知らせを入れる・切る。入れるときは OS の許可を取る。
   * 許可されなかったら false（設定は「切る」のまま）。
   */
  async setNoticeEnabled(enabled: boolean, now: Date = new Date()): Promise<boolean> {
    if (!this.canNotify) return false;
    if (!enabled) {
      this.store.update((d) => {
        d.noticeEnabled = false;
      });
      await this.platform.cancelNotices();
      return false;
    }
    const granted = await this.platform.requestNotificationPermission();
    track('notice_permission', { detail: granted ? 'granted' : 'denied' });
    this.store.update((d) => {
      d.noticeEnabled = granted;
      d.noticeAsked = true;
    });
    if (granted) await this.platform.scheduleNotices(buildDailyNotices(now));
    return granted;
  }

  /** 起動時：入れている人だけ、数日分を予約し直す（アプリの版で県が増えても追いつく）。 */
  async refreshNotices(now: Date = new Date()): Promise<void> {
    if (!this.canNotify || !this.store.data.noticeEnabled) return;
    await this.platform.scheduleNotices(buildDailyNotices(now));
  }
}
